import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Check, Share2, ChevronRight, Instagram, Facebook } from 'lucide-react'
import { PhotoSurface } from '../components/PhotoSurface'
import { ShotSelectLogo } from '../components/ShotSelectLogo'
import { useManifest } from '../state/manifest'
import { clientPolicyForManifest } from '../state/capabilities'
import { selectCounts, useSession } from '../state/session'
import { photographerShortName } from '../utils/photographerName'
import { trackShareEvent } from '../api/client'

export function Done() {
  const navigate = useNavigate()
  const manifest = useManifest((s) => s.manifest)
  const picks = useSession((s) => s.picks)
  const shareToken = useSession((s) => s.shareToken)
  const reset = useSession((s) => s.reset)
  const counts = selectCounts(picks)

  const collage = useMemo(() => {
    if (!manifest) return []
    return Object.values(picks)
      .filter((p) => p.bucket === 'love')
      .map((p) => manifest.photos.find((ph) => ph.id === p.photoId))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .slice(0, 4)
  }, [picks, manifest])

  if (!manifest) return null

  const padded = [...collage]
  while (padded.length < 4 && padded.length < manifest.photos.length) {
    padded.push(manifest.photos[padded.length])
  }

  const photographerName = photographerShortName(manifest.photographer.name)
  // A delivery-approval gallery reaches Done via the Approve / Request
  // changes sign-off, not a picks submission — so the recap copy is about the
  // sign-off, not "your N selected photos".
  const isDelivery = clientPolicyForManifest(manifest).can.approveDelivery

  const onShare = async () => {
    if (shareToken) {
      void trackShareEvent(shareToken, 'share_sheet_opened', {
        metadata: {
          loved: counts.love,
          maybe: counts.maybe,
        },
      })
    }
    const url = window.location.href
    const text = `My selected photos from our shoot with ${manifest.photographer.name}`
    if (navigator.share) {
      try {
        await navigator.share({ url, text, title: 'My selected photos' })
      } catch {
        /* user cancelled */
      }
    } else {
      await navigator.clipboard.writeText(url).catch(() => {})
    }
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <header className="flex items-center gap-2.5 px-4 pt-[max(3rem,calc(env(safe-area-inset-top)+1.5rem))] md:pt-6 pb-3 flex-shrink-0">
        {manifest.photographer.brandLogoUrl ? (
          <img
            src={manifest.photographer.brandLogoUrl}
            className="w-7 h-7 rounded-lg object-contain border border-[var(--border-default)] bg-[var(--surface-tertiary)]"
            alt=""
          />
        ) : (
          <div className="w-7 h-7 rounded-lg bg-[var(--surface-tertiary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-primary)]">
            <ShotSelectLogo className="h-4 w-4" />
          </div>
        )}
        <div className="leading-tight">
          <div className="text-[13px] font-semibold">{manifest.photographer.name}</div>
          <div className="text-[12px] uppercase tracking-wider text-[var(--text-tertiary)]">
            Sent · just now
          </div>
        </div>
      </header>

      <main className="flex flex-col flex-1">
        <div className="mx-4 mt-2 grid grid-cols-2 grid-rows-2 gap-1.5 rounded-3xl overflow-hidden h-[260px]">
          {padded.map((photo, i) => (
            <PhotoSurface
              key={`${photo.id}-${i}`}
              src={photo.thumbUrl}
              alt=""
              rounded="rounded-md"
              className="relative"
            />
          ))}
        </div>

        <div className="px-6 pt-7 pb-4 text-center flex flex-col items-center flex-1">
          <div className="w-14 h-14 rounded-full bg-[var(--accent-success-soft)] border border-[var(--accent-success-ring)] grid place-items-center text-[var(--accent-success)] mb-4">
            <Check size={28} strokeWidth={2.5} />
          </div>
          <h3 className="text-2xl font-semibold tracking-tight">Sent to {photographerName}</h3>
          <p className="text-[13px] text-[var(--text-secondary)] mt-2 max-w-xs">
            {isDelivery
              ? `Your response is on its way to ${photographerName}. They'll be in touch with the next steps soon.`
              : `Your ${counts.love} selected photos are on their way. They'll be in touch with the next steps soon.`}
          </p>
        </div>

        {manifest.photographer.email && (
          <div className="mx-4 px-4 py-3.5 bg-[var(--surface-secondary)] border border-[var(--border-subtle)] rounded-2xl flex items-center gap-3 flex-shrink-0">
            <div
              className="w-10 h-10 rounded-full bg-[var(--surface-tertiary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]"
              aria-hidden
            >
              <Camera size={16} strokeWidth={2} />
            </div>
            <div className="flex-1 leading-tight">
              <div className="text-[13px] font-semibold">
                {manifest.photographer.name.replace(' Photography', '')}
              </div>
              <div className="text-[12px] text-[var(--text-tertiary)] mt-0.5 truncate">
                {manifest.photographer.email}
              </div>
            </div>
            <ChevronRight size={16} className="text-[var(--text-tertiary)]" />
          </div>
        )}

        {(manifest.photographer.instagramUrl || manifest.photographer.facebookUrl) && (
          <div
            data-testid="gallery-social-links"
            className="mx-4 mt-3 flex items-center justify-center gap-3 flex-shrink-0"
          >
            {manifest.photographer.instagramUrl && (
              <a
                href={manifest.photographer.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${photographerName} on Instagram`}
                className="w-11 h-11 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]"
              >
                <Instagram size={16} strokeWidth={2} />
              </a>
            )}
            {manifest.photographer.facebookUrl && (
              <a
                href={manifest.photographer.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${photographerName} on Facebook`}
                className="w-11 h-11 rounded-full bg-[var(--surface-secondary)] border border-[var(--border-default)] grid place-items-center text-[var(--text-secondary)]"
              >
                <Facebook size={16} strokeWidth={2} />
              </a>
            )}
          </div>
        )}

        <div
          className="mx-4 mt-3.5 mb-7"
          style={{ marginBottom: 'max(1.75rem, calc(env(safe-area-inset-bottom) + 0.75rem))' }}
        >
          <button
            onClick={onShare}
            className="w-full h-12 rounded-2xl bg-[var(--surface-secondary)] border border-[var(--border-default)] text-sm font-medium text-[var(--text-primary)] flex items-center justify-center gap-2"
          >
            <Share2 size={16} strokeWidth={2} />
            Share your selected photos
          </button>
          <button
            onClick={() => {
              reset()
              navigate('/')
            }}
            className="w-full mt-2 h-11 rounded-xl text-xs text-[var(--text-tertiary)] underline underline-offset-2"
          >
            Start over
          </button>
        </div>
      </main>
    </div>
  )
}
